import {ChangeEvent, forwardRef, ReactNode, useCallback, useMemo} from 'react'
import {Box, Paper, keyframes} from '@mui/material'
import {useBooleanState} from '../hooks/useBooleanState'
import {Validators} from '../../utils/Validators'

export interface DigitInputProps {
  length: number
  value: string
  onChange: (value: string) => void
  disabled?: boolean
  className?: string
  autoFocus?: boolean
  id?: string
}

export const DigitInput = forwardRef<HTMLInputElement, DigitInputProps>(
  ({length, onChange, value, className, disabled, autoFocus, id}: DigitInputProps, ref) => {
    const {enableState: onFocus, disableState: onBlur, state: isFocused} = useBooleanState(false)

    const isItemFocused = useCallback(
      (index: number) => {
        const isItemFocused = value.length === index && isFocused
        return isItemFocused
      },
      [isFocused, value.length]
    )

    const getValue = useCallback(
      (index: number, isItemFocused: boolean) => {
        const itemValue = value[index]
        if (itemValue) {
          return itemValue
        } else if (isItemFocused) {
          return '-'
        } else {
          return PASSWORD_DOT
        }
      },
      [value]
    )

    const digits = useMemo(() => {
      const digitNodes: ReactNode[] = []

      for (let i = 0; i < length; i++) {
        const isFocused = isItemFocused(i)
        digitNodes.push(
          <Paper
            elevation={0}
            variant='outlined'
            sx={(theme) => ({
              display: 'inline-flex',
              justifyContent: 'center',
              alignItems: 'center',
              fontSize: '2rem',
              width: '3rem',
              height: '3rem',
              '&:not(:last-child)': {
                mr: theme.spacing(1),
              },
            })}
            key={i}
          >
            <Box
              sx={{
                display: 'inline',
                // transform: 'translateY(-0.1em)',
                animation: isFocused ? `${blink} 0.5s infinite` : undefined,
              }}
            >
              {getValue(i, isFocused)}
            </Box>
          </Paper>
        )
      }

      return digitNodes
    }, [getValue, isItemFocused, length])

    const handleChange = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        if (Validators.WHOLE_NUMBER_REGEX.test(value) && value.length <= length) {
          onChange(value)
        }
      },
      [onChange, length]
    )

    return (
      <div className={className}>
        <Box component='label' sx={{display: 'flex', justifyContent: 'center'}}>
          {digits}
          <Box
            id={id}
            component='input'
            ref={ref}
            autoFocus={autoFocus}
            onFocus={onFocus}
            onBlur={onBlur}
            disabled={disabled}
            type='number'
            onChange={handleChange}
            value={value}
            sx={{opacity: 0, position: 'absolute', height: 0, width: 0}}
          />
        </Box>
      </div>
    )
  }
)

export const PASSWORD_DOT = '●'

const blink = keyframes`
  0% {
    opacity: 1;
  }
  40% {
    opacity: 0;
  }
  60% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`
